/**
 * Thin fetch wrappers for the scoreboard Worker API (worker/index.ts).
 */

import type { ScoreProgress } from '@/score/formula';

export interface ProgressSubmission extends ScoreProgress {
  playerId: string;
  name: string;
}

export interface BoardRow {
  rank: number;
  playerId: string;
  name: string;
  tier: number;
  /** Time to finish the furthest finished level; null while on level 1. */
  finishTimeSec: number | null;
  score: number;
  cellMaxBiomass: number;
  multicellMaxBiomass: number;
  dna: number;
  achievements: number;
}

export interface LeaderboardResponse {
  rows: BoardRow[];
  me: BoardRow | null;
  total: number;
}

/** status 0 = network failure. 4xx = the server rejected the payload. */
export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      ...init,
      headers: { 'content-type': 'application/json' },
    });
  } catch (err) {
    throw new ApiError(0, String(err));
  }
  if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => ''));
  try {
    return (await res.json()) as T;
  } catch {
    throw new ApiError(0, 'Bad JSON from scoreboard API');
  }
}

export function submitProgress(s: ProgressSubmission): Promise<{ score: number; tier: number; rank: number }> {
  return request('/progress', { method: 'POST', body: JSON.stringify(s) });
}

/** Fire-and-forget variant for tab close — survives page unload. */
export function beaconProgress(s: ProgressSubmission): void {
  const body = new Blob([JSON.stringify(s)], { type: 'application/json' });
  navigator.sendBeacon?.('/api/progress', body);
}

export function fetchLeaderboard(playerId: string): Promise<LeaderboardResponse> {
  const q = new URLSearchParams({ player: playerId, limit: '50' });
  return request(`/leaderboard?${q}`);
}
